import { useRef, useEffect, useState } from 'react';
import SignaturePadLib from 'signature_pad';

interface SignaturePadProps {
  onSignatureChange: (dataUrl: string | null) => void;
}

export default function SignaturePad({ onSignatureChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const [hasSignature, setHasSignature] = useState(false);

  /* ---------------------------
     INIT + RESIZE
  ---------------------------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // Create the signature pad instance
    const pad = new SignaturePadLib(canvas, {
      penColor: '#1a1a2e',
      minWidth: 1.5,
      maxWidth: 3,
      velocityFilterWeight: 0.7,
      backgroundColor: 'rgba(0,0,0,0)',
    });

    padRef.current = pad;

    // Emit data URL whenever a stroke ends
    pad.addEventListener('endStroke', () => {
      setHasSignature(true);
      onSignatureChange(pad.toDataURL('image/png'));
    });

    // Size the canvas to its container
    const resize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // Save existing strokes
      const data = pad.toData();

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      canvas.getContext('2d')?.scale(dpr, dpr);

      // Restore strokes after resize
      if (data.length) {
        pad.fromData(data);
      }
    };

    resize();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      pad.off();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------------------
     CLEAR
  ---------------------------- */
  const clearSignature = () => {
    padRef.current?.clear();
    setHasSignature(false);
    onSignatureChange(null);
  };

  /* ---------------------------
     UI
  ---------------------------- */
  return (
    <div className="signature-pad-wrapper">
      <div className="signature-pad-header">
        <span className="signature-pad-label">Draw your signature</span>
        {hasSignature && (
          <button
            type="button"
            className="signature-clear-btn"
            onClick={clearSignature}
          >
            Clear
          </button>
        )}
      </div>

      <div
        ref={containerRef}
        className={`signature-pad-container ${hasSignature ? 'has-signature' : ''}`}
      >
        <canvas ref={canvasRef} className="signature-canvas" />

        {!hasSignature && (
          <div className="signature-placeholder">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              <path d="m15 5 4 4" />
            </svg>
            <span>Sign here</span>
          </div>
        )}

        <div className="signature-line" />
      </div>
    </div>
  );
}
